
import React, { useState, useEffect } from 'react';
import { useCTraderAccounts } from '@/hooks/use-ctrader-accounts';
import { useCTraderProfileValidation } from '@/hooks/use-ctrader-profile-validation';
import { useToast } from "@/hooks/use-toast";
import { ctraderProfileService } from '@/services/ctrader-profile-service';
import { useCTraderInvalidation } from '@/hooks/use-ctrader-invalidation';
import { Separator } from '@/components/ui/separator';
import { ConnectCTraderDialog } from '@/components/accounts/ConnectCTraderDialog';
import { EnhancedPageHeader } from '@/components/manage-ctrader/EnhancedPageHeader';
import { EnhancedSearchCreateToolbar } from '@/components/manage-ctrader/EnhancedSearchCreateToolbar';
import { EnhancedProfilesGrid } from '@/components/manage-ctrader/EnhancedProfilesGrid';
import { useLogoPreloader } from '@/hooks/useLogoPreloader';
import { Helmet } from 'react-helmet-async';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';

export default function ManageCTraderProfiles() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { data: accounts, isLoading } = useCTraderAccounts();
  const { toast } = useToast();
  const { canCreateProfile, getErrorMessage, currentCount, maxProfiles } = useCTraderProfileValidation();
  const { invalidateProfileQueries, invalidateResourceUsage } = useCTraderInvalidation();
  const isMobile = useIsMobile();
  const { user } = useAuth();

  // Preload essential logos
  useLogoPreloader();

  // Collect all platform logos for prefetching
  const platformLogos = React.useMemo(() => {
    if (!accounts) return [];
    
    return accounts.map(account => {
      const platformName = (account.broker || account.prop_firm || '').toLowerCase();
      switch(platformName) {
        case 'coinstrat pro':
          return 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20coinstrat%20pro.png';
        case 'fxpro':
          return 'https://play-lh.googleusercontent.com/z1qgps1oruxuJnPdEJjK_209y-TI2v0bTQeZf8DxKRz2cR0ULY5-YFzKknA3YzlB7g';
        case 'xm':
          return 'https://play-lh.googleusercontent.com/EPrP4-hkROiDGPmLnMSKaFlfWHCI5HdV1EWXXVnrHezxYK74czgVhPOxn-psu5_T6A';
        case 'ic market':
          return 'https://play-lh.googleusercontent.com/S_LUow-89YSbQdIf0cYK2qWLVOLGCKkRkj9PRlBHzWwE80-znbQZs95TH96cCIbhDA';
        case 'ftmo':
          return 'https://play-lh.googleusercontent.com/Zz9gWxJt7C8wPZ1jd-8O9HMWAkH4KO8OfKvwPinpvZPtYs4RcbC1ckwZQPj4HNB8GQ';
        case 'ai prop':
          return '/lovable-uploads/d8c04af7-018a-4e74-8091-9940d7e03ad7.png';
        default:
          return 'https://play-lh.googleusercontent.com/rZ6p-VXduWWqsOBGpwsyK_ZpNVOYgj8bKlqQGqPbtVwsTAfVk67s3cSLxuXctJBPzZU';
      }
    });
  }, [accounts]);

  const handleCreateProfile = () => {
    if (!canCreateProfile()) {
      toast({
        title: "Profile Limit Reached",
        description: getErrorMessage(),
        variant: "destructive"
      });
      return;
    }
    setIsCreateDialogOpen(true);
  };

  const handleDisconnect = async (id: string) => {
    try {
      await ctraderProfileService.disconnectProfile(id);
      
      toast({
        title: "Account disconnected",
        description: "The cTrader profile and all associated trading accounts have been disconnected."
      });
      
      await invalidateProfileQueries(id);
      
    } catch (error) {
      console.error('Error disconnecting accounts:', error);
      toast({
        title: "Error",
        description: "Failed to disconnect accounts. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleReconnect = async (id: string) => {
    try {
      await ctraderProfileService.reconnectProfile(id);
      
      toast({
        title: "Account reconnected",
        description: "The cTrader profile and all associated trading accounts have been reconnected."
      });
      
      await invalidateProfileQueries(id);
      
    } catch (error) {
      console.error('Error reconnecting accounts:', error);
      toast({
        title: "Error",
        description: "Failed to reconnect accounts. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await ctraderProfileService.deleteProfile(id);
      
      toast({
        title: "Profile deleted",
        description: "The cTrader profile has been deleted successfully."
      });
      
      await invalidateProfileQueries(id);
      invalidateResourceUsage();
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete profile",
        variant: "destructive"
      });
    }
  };

  const filteredAccounts = accounts?.filter(account => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = 
      account.csp_account_name.toLowerCase().includes(query) ||
      account.csp_account_email.toLowerCase().includes(query);
    
    const matchesStatus = 
      statusFilter === 'all' || account.status === statusFilter;
      
    return matchesSearch && matchesStatus;
  });

  const handleCreateProfileSuccess = () => {
    setIsCreateDialogOpen(false);
    invalidateResourceUsage();
  };

  return (
    <>
      <Helmet>
        {!isLoading && platformLogos && platformLogos.slice(0, 3).map((logo, index) => (
          <link key={`preload-${index}`} rel="preload" href={logo} as="image" />
        ))}
        <link rel="preconnect" href="https://play-lh.googleusercontent.com" />
        <link rel="preconnect" href="https://lbvhywxvthwgiebjsrlr.supabase.co" />
      </Helmet>
      
      <div className="space-y-4 md:space-y-6 p-3 md:p-0">
        <EnhancedPageHeader />
        
        <div data-tour="search-create">
          <EnhancedSearchCreateToolbar 
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onCreateClick={handleCreateProfile}
            canCreateProfile={canCreateProfile()}
            currentCount={currentCount}
            maxProfiles={maxProfiles}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        </div>

        <Separator className="bg-gradient-to-r from-transparent via-border to-transparent" />

        <div data-tour="profiles-grid" className="pb-4 md:pb-6">
          <EnhancedProfilesGrid 
            isLoading={isLoading}
            profiles={filteredAccounts}
            onDisconnect={handleDisconnect}
            onReconnect={handleReconnect}
            onDelete={handleDelete}
            onCreateProfile={handleCreateProfile}
            searchQuery={searchQuery}
          />
        </div>

        <ConnectCTraderDialog 
          open={isCreateDialogOpen} 
          onOpenChange={(open) => {
            setIsCreateDialogOpen(open);
            if (!open) {
              // Refresh data when dialog closes after successful creation
              invalidateResourceUsage();
            }
          }}
        />
      </div>
    </>
  );
}
